// src/components/charts/base/EChartsRenderer.tsx

import React from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

interface EChartsRendererProps {
  chartOption: EChartsOption;
  height: number | string;
  isLoading?: boolean;
  error?: string | null;
  hasData?: boolean;
  className?: string;
  /**
   * Optional container class override. When provided, it replaces the default
   * container styling (background, border, rounding). Useful to render charts
   * on transparent or custom surfaces.
   */
  containerClassName?: string;
}

/**
 * Pure ECharts Rendering Component
 *
 * Handles the actual chart rendering with proper loading, error, and empty states.
 * This component is memoized for performance when rendering multiple charts.
 *
 * States handled:
 * - Loading: Shows spinner with chart skeleton
 * - Error: Shows error message with retry-friendly UI
 * - No Data: Shows empty state with helpful messaging
 * - Success: Renders the actual chart
 *
 * Responsive design:
 * - Chart automatically adapts to container width
 * - Height is controlled via props
 * - Maintains aspect ratio on all screen sizes
 */
const EChartsRenderer: React.FC<EChartsRendererProps> = React.memo(
  ({
    chartOption,
    height,
    isLoading = false,
    error = null,
    hasData = true,
    className = "",
    containerClassName,
  }) => {
    const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
    const defaultContainer = "bg-base-100 border border-base-300 rounded-lg overflow-hidden";
    const containerClasses = `${containerClassName ?? defaultContainer} ${className}`.trim();

    // Loading State
    if (isLoading) {
      return (
        <div
          className={`${containerClasses} flex items-center justify-center`}
          style={{ height: resolvedHeight }}>
          <div className="text-center">
            <div className="loading loading-spinner loading-lg text-primary mb-4"></div>
            <p className="text-base-content/60 text-sm">
              Loading chart data...
            </p>
          </div>
        </div>
      );
    }

    // Error State
    if (error) {
      return (
        <div
          className={`${containerClasses} flex items-center justify-center`}
          style={{ height: resolvedHeight }}>
          <div className="text-center p-6">
            <svg
              className="mx-auto h-12 w-12 text-error mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 48 48">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-error mb-2">Chart Error</h3>
            <p className="text-error/80 text-sm max-w-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-sm btn-error btn-outline mt-4">
              Try Again
            </button>
          </div>
        </div>
      );
    }

    // No Data State
    if (!hasData) {
      return (
        <div
          className={`${containerClasses} flex items-center justify-center`}
          style={{ height: resolvedHeight }}>
          <div className="text-center p-6">
            <svg
              className="mx-auto h-12 w-12 text-blue-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 48 48">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v6a2 2 0 002 2h1l1 1v-6a2 2 0 00-2-2H9zM9 19a2 2 0 012-2h1v-2a2 2 0 012-2h6a2 2 0 012 2v2h1a2 2 0 012 2v6a2 2 0 01-2 2h-1l-1 1v2a2 2 0 01-2 2h-6a2 2 0 01-2-2v-2H11a2 2 0 01-2-2v-6z"
              />
            </svg>
            <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-2">
              No Data Available
            </h3>
            <p className="text-blue-600 dark:text-blue-300 text-sm">
              Select tokens above to start visualizing chart data
            </p>
          </div>
        </div>
      );
    }

    // Success State - Render Chart
    const fillParent = height === "100%";
    return (
      <div className={`${containerClasses}${fillParent ? " h-full" : ""}`}>
        <ReactECharts
          option={chartOption}
          style={{
            height: resolvedHeight,
            width: "100%",
          }}
          opts={{
            renderer: "canvas",
            devicePixelRatio: window.devicePixelRatio || 1,
          }}
          lazyUpdate={true}
          notMerge={false}
          theme="auto"
        />
      </div>
    );
  }
);

// Set display name for debugging
EChartsRenderer.displayName = "EChartsRenderer";

export default EChartsRenderer;

